import {
  WSButton,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import { IAddress } from "@wingspanhq/users/dist/lib/interfaces";
import type { cardApiSpec } from "@wingspanhq/wingspan-api-types";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import React from "react";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useMutationCreateCard } from "../../../query/cards/mutations/useMutationCreateCard";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryMemberProfile } from "../../../query/users/queries";
import { WSQueries } from "../../../query/WSQuery";
import { ErrorContextKey } from "../../../services/platform";
import { AddressText } from "../../../shared/components/AddressText/AddressText";
import { FieldView } from "../../../shared/components/FieldView/FieldView";
import { useUpdateBillingAddressModal } from "../modals/OrderConfirmation/useUpdateBillingAddress";
import { selectorCardShippingAddress } from "../selectors/selectorCardShippingAddress";
import { PreviewPhysicalCard } from "./PreviewPhysicalCard";

type Props = {
  onBack?: () => void;
  onOrdered: (card: cardApiSpec.Card) => void | Promise<void>;
};

export const ConfirmOrderPhysicalCard: React.FC<Props> = ({
  onBack,
  onOrdered
}) => {
  const userId = useUserId();
  const memberProfileQuery = useQueryMemberProfile(userId);
  const [createCard, createCardMeta] = useMutationCreateCard();
  const updateBillingAddressModal = useUpdateBillingAddressModal();
  const [shippingAddress, setShippingAddress] = React.useState<IAddress | null>(
    null
  );

  const [error, setError] = React.useState<string>();

  return (
    <WSQueries queries={{ memberProfileQuery }}>
      {({ memberProfileQueryData }) => {
        const names = wsName(memberProfileQueryData);
        const address =
          shippingAddress || memberProfileQueryData.profile.address;

        return (
          <WSList gap="L">
            <WSText.Paragraph weight="medium">
              Order a free Wingspan business debit card
            </WSText.Paragraph>
            <PreviewPhysicalCard />
            <WSText.Paragraph weight="medium">
              Confirm your information
            </WSText.Paragraph>
            <WSText.ParagraphSm color="gray500">
              Please review the information we have on file. Confirm or update
              the mailing address where the card will be sent.
            </WSText.ParagraphSm>
            <WSPanel colorBackground="white">
              <WSGrid gutter={{ m: "XL" }}>
                <WSGrid.Item span={{ m: "6" }}>
                  <FieldView
                    label="Legal first name"
                    value={names.firstName}
                  ></FieldView>
                </WSGrid.Item>
                <WSGrid.Item span={{ m: "6" }}>
                  <FieldView
                    label="Legal last name"
                    value={names.lastName}
                  ></FieldView>
                </WSGrid.Item>
                <WSGrid.Item span={{ m: "12" }}>
                  <WSFlexBox justify="space-between">
                    <FieldView
                      label="Address"
                      value={
                        <WSText.ParagraphSm weight="book" singleLine={false}>
                          <AddressText {...address} />
                        </WSText.ParagraphSm>
                      }
                    />
                    <WSButton.Link
                      size="M"
                      onClick={() => {
                        updateBillingAddressModal.open({
                          address,
                          onSuccess: newAddress => {
                            setError(undefined);
                            setShippingAddress(newAddress);
                          }
                        });
                      }}
                    >
                      Edit
                    </WSButton.Link>
                  </WSFlexBox>
                </WSGrid.Item>
              </WSGrid>
            </WSPanel>
            <WSErrorMessage
              mb="XL"
              error={error || createCardMeta.error}
              contextKey={ErrorContextKey.BankCard}
            />
            <WSButton.Primary
              mt="XL"
              fullWidth
              size="M"
              onAsyncClick={async () => {
                const cardShippingAddress =
                  selectorCardShippingAddress(address);

                if (cardShippingAddress) {
                  await createCard(
                    {
                      userId,
                      isVirtualCard: false,
                      address: cardShippingAddress
                    },
                    { onSuccess: onOrdered }
                  );
                } else {
                  setError("Invalid address. Please update your address.");
                }
              }}
            >
              Confirm & Continue
            </WSButton.Primary>
            {onBack && (
              <WSButton.Secondary fullWidth size="M" onClick={onBack}>
                Back
              </WSButton.Secondary>
            )}
          </WSList>
        );
      }}
    </WSQueries>
  );
};
